import React from 'react';
import { Link } from 'react-router-dom';
import ParrotBrandMark from '../components/ParrotBrandMark';
import {
  CONTACT_EMAIL,
  EFFECTIVE_DATE,
  privacySections,
  termsSections,
} from './legalContent';
import type { LegalSection } from './legalContent';

type LegalDocumentKind = 'terms' | 'privacy';

const documentMeta: Record<
  LegalDocumentKind,
  {
    anchor: string;
    eyebrow: string;
    title: string;
    description: string;
    sections: LegalSection[];
  }
> = {
  terms: {
    anchor: 'terms',
    eyebrow: 'Terms and Agreements',
    title: 'Terms of Service',
    description:
      'The rules for using Parrot, including accounts, subscriptions, user content, automated feedback, Apple terms, and liability limits.',
    sections: termsSections,
  },
  privacy: {
    anchor: 'privacy',
    eyebrow: 'Privacy Policy',
    title: 'Privacy Policy',
    description:
      'How Parrot collects, uses, shares, retains, and protects information for the app, website, subscriptions, support, and speech practice features.',
    sections: privacySections,
  },
};

const Legal = () => (
  <LegalShell
    title="Legal"
    description="Terms of Service and Privacy Policy for Parrot."
    showCombinedNav
  >
    <DocumentBlock kind="terms" />
    <DocumentBlock kind="privacy" muted />
  </LegalShell>
);

export const LegalSinglePage: React.FC<{ kind: LegalDocumentKind }> = ({ kind }) => {
  const meta = documentMeta[kind];

  return (
    <LegalShell title={meta.title} description={meta.description}>
      <DocumentBlock kind={kind} />
    </LegalShell>
  );
};

type LegalShellProps = {
  title: string;
  description: string;
  showCombinedNav?: boolean;
  children: React.ReactNode;
};

const LegalShell: React.FC<LegalShellProps> = ({
  title,
  description,
  showCombinedNav = false,
  children,
}) => {
  useHashScroll();

  return (
    <div className="min-h-screen bg-parrot-yellow text-parrot-black">
      <header className="px-6 pb-5 pt-8 sm:px-16">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" aria-label="Back to Parrot home">
            <ParrotBrandMark className="h-12 w-12" />
            <span className="text-sm font-bold tracking-[0.22em]">PARROT</span>
          </Link>

          <nav className="hidden items-center gap-5 text-sm font-semibold sm:flex">
            {showCombinedNav ? (
              <>
                <a href="#terms" className="hover:text-parrot-blue">
                  Terms
                </a>
                <a href="#privacy" className="hover:text-parrot-blue">
                  Privacy
                </a>
              </>
            ) : (
              <Link to="/legal" className="hover:text-parrot-blue">
                All Legal
              </Link>
            )}
            <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-parrot-blue">
              Contact
            </a>
          </nav>
        </div>
      </header>

      <main>
        <section className="px-6 pb-12 pt-8 sm:px-16 sm:pb-16 sm:pt-12">
          <div className="mx-auto max-w-7xl">
            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-parrot-black/60">
              Parrot Legal
            </p>
            <h1 className="mt-4 text-[56px] font-black leading-none tracking-tight sm:text-[92px]">
              {title}
            </h1>
            <p className="mt-6 max-w-3xl text-lg font-semibold leading-8 text-parrot-black/75">
              {description}
            </p>
            <p className="mt-5 text-sm font-semibold text-parrot-black/60">
              Effective date: {EFFECTIVE_DATE}
            </p>

            {showCombinedNav && (
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#terms"
                  className="rounded-full bg-parrot-black px-5 py-3 text-sm font-black uppercase tracking-[0.16em] text-white transition-transform hover:scale-[1.02]"
                >
                  Terms
                </a>
                <a
                  href="#privacy"
                  className="rounded-full border border-parrot-black/20 bg-white px-5 py-3 text-sm font-black uppercase tracking-[0.16em] text-parrot-black transition-colors hover:border-parrot-blue hover:text-parrot-blue"
                >
                  Privacy
                </a>
              </div>
            )}
          </div>
        </section>

        {children}
      </main>

      <footer className="border-t border-parrot-black/10 px-6 py-6 sm:px-16">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 sm:flex-row">
          <p className="text-sm text-parrot-black/70">
            © {new Date().getFullYear()} Parrot. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/help" className="text-sm text-parrot-black/80 hover:text-parrot-blue">
              Help
            </Link>
            <Link to="/legal" className="text-sm text-parrot-black/80 hover:text-parrot-blue">
              Legal
            </Link>
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-sm text-parrot-black/80 hover:text-parrot-blue">
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

const useHashScroll = () => {
  React.useEffect(() => {
    if (!window.location.hash) return;

    const targetId = window.location.hash.slice(1);
    window.requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView();
    });
  }, []);
};

const DocumentBlock: React.FC<{ kind: LegalDocumentKind; muted?: boolean }> = ({
  kind,
  muted = false,
}) => {
  const meta = documentMeta[kind];

  return (
    <section
      id={meta.anchor}
      className={`scroll-mt-8 px-6 py-14 sm:px-16 sm:py-16 ${
        muted ? 'bg-[#F7F7F4]' : 'bg-white'
      }`}
    >
      <div className="mx-auto max-w-4xl">
        <p className="text-[11px] font-black uppercase tracking-[0.24em] text-parrot-black/50">
          {meta.eyebrow}
        </p>
        <h2 className="mt-3 text-4xl font-black leading-tight sm:text-5xl">
          {meta.title}
        </h2>
        <p className="mt-4 text-base leading-7 text-parrot-black/70">
          {meta.description}
        </p>
        <p className="mt-3 text-sm font-semibold text-parrot-black/50">
          Effective date: {EFFECTIVE_DATE}
        </p>

        <div className="mt-10 space-y-8">
          {meta.sections.map((section) => (
            <LegalSectionBlock key={section.id} section={section} />
          ))}
        </div>
      </div>
    </section>
  );
};

const LegalSectionBlock: React.FC<{ section: LegalSection }> = ({ section }) => (
  <section id={section.id} className="scroll-mt-8">
    <h3 className="text-xl font-black leading-snug">{section.title}</h3>
    <div className="mt-3 space-y-4 text-[15px] leading-7 text-parrot-black/80">
      {section.content}
    </div>
  </section>
);

export default Legal;
